import { Button, Collapse, InputGroup, Intent } from '@blueprintjs/core'
import { useState } from 'react'

const DEFAULT_BRACKET_BOUNDARIES = [5, 10, 20, 40, 60, 80, 90, 95]
const DEFAULT_OVER_DIFFS = [4, 5, 5, 5, 7, 8, 8, 9, 10]
const DEFAULT_UNDER_DIFFS = [1, 2, 3, 3, 5, 7, 8, 50, 50]
const DEFAULT_Z_THRESHOLDS = [1.28, 1.96, 2.58, 3.29]

function erfc(x) {
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741
  const a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911
  const sign = x < 0 ? -1 : 1
  x = Math.abs(x)
  const t = 1.0 / (1.0 + p * x)
  const y = 1.0 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x)
  return 1 - sign * y
}

function zToConfidence(z) {
  const p = erfc(Math.abs(z) / Math.sqrt(2))
  return Math.round((1 - p) * 100)
}

function buildBracketLabels(boundaries) {
  const labels = [`≤ ${boundaries[0]}%`]
  for (let i = 0; i < boundaries.length - 1; i++) {
    labels.push(`${boundaries[i]}-${boundaries[i + 1]}%`)
  }
  labels.push(`> ${boundaries[boundaries.length - 1]}%`)
  return labels
}

export default function ControlsPanel({ onApply, loading }) {
  const [isOpen, setIsOpen] = useState(true)
  const [boundaries, setBoundaries] = useState(DEFAULT_BRACKET_BOUNDARIES)
  const [overDiffs, setOverDiffs] = useState(DEFAULT_OVER_DIFFS)
  const [underDiffs, setUnderDiffs] = useState(DEFAULT_UNDER_DIFFS)
  const [zThresholds, setZThresholds] = useState(DEFAULT_Z_THRESHOLDS)

  const updateBoundary = (idx, val) => {
    const next = [...boundaries]
    next[idx] = parseFloat(val) || 0
    setBoundaries(next)
  }

  const updateOverDiff = (idx, val) => {
    const next = [...overDiffs]
    next[idx] = parseFloat(val) || 0
    setOverDiffs(next)
  }

  const updateUnderDiff = (idx, val) => {
    const next = [...underDiffs]
    next[idx] = parseFloat(val) || 0
    setUnderDiffs(next)
  }

  const updateZ = (idx, val) => {
    const next = [...zThresholds]
    next[idx] = parseFloat(val) || 0
    setZThresholds(next)
  }

  const addBoundary = () => {
    const last = boundaries[boundaries.length - 1]
    const newBoundary = last < 99 ? last + 1 : last
    setBoundaries([...boundaries, newBoundary].sort((a, b) => a - b))
    // Add corresponding diff values (copy the last bracket's values)
    setOverDiffs([...overDiffs, overDiffs[overDiffs.length - 1]])
    setUnderDiffs([...underDiffs, underDiffs[underDiffs.length - 1]])
  }

  const removeBoundary = (idx) => {
    if (boundaries.length <= 1) return
    setBoundaries(boundaries.filter((_, i) => i !== idx))
    // Remove the diff at idx+1 (the bracket that gets merged)
    setOverDiffs(overDiffs.filter((_, i) => i !== idx + 1))
    setUnderDiffs(underDiffs.filter((_, i) => i !== idx + 1))
  }

  const handleApply = () => {
    onApply({
      bracket_boundaries: boundaries.map((b) => b / 100),
      over_diffs_pp: overDiffs,
      under_diffs_pp: underDiffs,
      z_thresholds: zThresholds,
    })
  }

  const handleReset = () => {
    setBoundaries(DEFAULT_BRACKET_BOUNDARIES)
    setOverDiffs(DEFAULT_OVER_DIFFS)
    setUnderDiffs(DEFAULT_UNDER_DIFFS)
    setZThresholds(DEFAULT_Z_THRESHOLDS)
    onApply({
      bracket_boundaries: DEFAULT_BRACKET_BOUNDARIES.map((b) => b / 100),
      over_diffs_pp: DEFAULT_OVER_DIFFS,
      under_diffs_pp: DEFAULT_UNDER_DIFFS,
      z_thresholds: DEFAULT_Z_THRESHOLDS,
    })
  }

  const isStrictlyIncreasing = boundaries.every((val, i) => i === 0 || val > boundaries[i - 1])
  const isInRange = boundaries.every((val) => val > 0 && val < 100)
  const boundariesValid = isStrictlyIncreasing && isInRange
  const validationError = !boundariesValid
    ? !isInRange ? 'Boundaries must be between 0 and 100.' : 'Boundaries must be strictly increasing.'
    : null

  const bracketLabels = buildBracketLabels(boundaries)

  return (
    <div style={{ padding: '12px 20px', borderBottom: '1px solid #ddd', background: '#f8f9fa' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Threshold Controls</h3>
        <Button minimal icon={isOpen ? 'chevron-up' : 'chevron-down'} onClick={() => setIsOpen(!isOpen)} />
      </div>
      <Collapse isOpen={isOpen}>
        <div style={{ display: 'flex', gap: '60px', marginTop: '12px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ minWidth: '280px' }}>
            <h4 style={{ margin: '0 0 8px 0' }}>Min diff per base rate bracket (pp)</h4>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <span style={{ minWidth: '100px' }}></span>
              <span style={{ width: '70px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', color: '#298440' }}>Over</span>
              <span style={{ width: '70px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', color: '#7F3C8D' }}>Under</span>
            </div>
            {bracketLabels.map((label, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', whiteSpace: 'nowrap' }}>
                <span style={{ minWidth: '100px', textAlign: 'right' }}>{label}:</span>
                <InputGroup
                  small
                  type="number"
                  value={overDiffs[i]}
                  onChange={(e) => updateOverDiff(i, e.target.value)}
                  style={{ width: '70px' }}
                />
                <InputGroup
                  small
                  type="number"
                  value={underDiffs[i]}
                  onChange={(e) => updateUnderDiff(i, e.target.value)}
                  style={{ width: '70px' }}
                />
              </div>
            ))}
          </div>

          <div style={{ minWidth: '200px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <h4 style={{ margin: 0 }}>Base rate bracket boundaries (%)</h4>
              <Button small minimal icon="plus" onClick={addBoundary} />
            </div>
            <div>
              {boundaries.map((val, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', whiteSpace: 'nowrap' }}>
                  <span style={{ minWidth: '100px', textAlign: 'right' }}>Boundary {i + 1}:</span>
                  <InputGroup
                    small
                    type="number"
                    value={val}
                    onChange={(e) => updateBoundary(i, e.target.value)}
                    style={{ width: '70px' }}
                  />
                  <Button
                    small
                    minimal
                    icon="cross"
                    onClick={() => removeBoundary(i)}
                    disabled={boundaries.length <= 1}
                  />
                </div>
              ))}
            </div>
          </div>

          <div style={{ minWidth: '250px' }}>
            <h4 style={{ margin: '0 0 8px 0' }}>Z-score bin thresholds</h4>
            {zThresholds.map((val, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', whiteSpace: 'nowrap' }}>
                <span style={{ minWidth: '30px', textAlign: 'right' }}>±{i + 1}:</span>
                <InputGroup
                  small
                  type="number"
                  step="0.01"
                  value={val}
                  onChange={(e) => updateZ(i, e.target.value)}
                  style={{ width: '70px' }}
                />
                <span style={{ color: '#888', fontSize: '12px' }}>({zToConfidence(val)}% conf.)</span>
              </div>
            ))}
          </div>
        </div>

        {validationError && (
          <div style={{ color: '#c23030', fontSize: '12px', marginTop: '8px' }}>{validationError}</div>
        )}
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <Button
            intent={Intent.PRIMARY}
            text="Apply"
            onClick={handleApply}
            loading={loading}
            disabled={!boundariesValid}
          />
          <Button
            minimal
            text="Reset to defaults"
            onClick={handleReset}
          />
        </div>
      </Collapse>
    </div>
  )
}
